import { ProducedArtifact } from "@/lib/artifact";
import { SearchPeopleLiveRow } from "@shaarp/common";
import { useSyncExternalStore } from "react";

/** Only a successful call has anything to show the panel. */
type ActiveArtifact = ProducedArtifact;

/**
 * Progress of a people search still running, so the panel shows its table
 * filling up. Cleared once the search completes, which hands the table over to
 * the saved (enriched) rows.
 */
export type PeopleTableLive = {
  /** What the search looks for, shown while it has found nobody yet. */
  query: string;
  /** People in the table so far. */
  peopleCount: number;
  /** The people this run found so far. */
  rows: SearchPeopleLiveRow[];
  /** The latest thing the search said it was doing. */
  status: string | null;
};

/**
 * What the chat's side panel shows: one thing at a time, a document or a table
 * of people. Opening one replaces the other, as clicking another document
 * always did.
 */
export type ChatSidePanel =
  | { kind: "artifact"; artifact: ActiveArtifact }
  | { kind: "peopleTable"; searchId: string; live: PeopleTableLive | null };

type PeopleTablePanel = Extract<ChatSidePanel, { kind: "peopleTable" }>;

/** Whether the panel shows the people table of `searchId`. */
const isPeopleTableShown = (
  panel: ChatSidePanel | null,
  searchId: string,
): panel is PeopleTablePanel =>
  panel?.kind === "peopleTable" && panel.searchId === searchId;

/**
 * Module-level store tracking what the chat's side panel shows, mirroring
 * `companyPreviewPanelStore` — outside React so the panel can be opened from
 * deep inside the message tree (an inline tool card) without threading a
 * callback through `ChatConversation` → `ChatConversationView` →
 * `ChatMessages` → `ChatMessage`.
 *
 * Deliberately single-slot (D-12): a conversation may accumulate several
 * Artifacts and tables, but the panel always shows the last one produced or
 * clicked — the thread itself serves as the history, one card each.
 */
export const chatArtifactPanelStore = {
  panel: null as ChatSidePanel | null,
  /**
   * The latest version seen of each document, by `artifactId`. An edit
   * rewrites the same stored object, so an older card's URL is one the
   * browser may have cached before the edit: opening a card goes through
   * this instead of the card's own output.
   */
  latestById: new Map<string, ActiveArtifact>(),
  /**
   * When each version produced live reached the browser, by its URL. That
   * URL was signed seconds before; the API can only sign another once the
   * turn ends and the document is attached to its message, so the panel
   * shows this one until it is due for renewal (`useArtifactUrl`).
   */
  publishedAtByUrl: new Map<string, number>(),
  listeners: new Set<() => void>(),
  /** The document shown, null when the panel shows a table or nothing. */
  getSnapshot(): ActiveArtifact | null {
    const { panel } = chatArtifactPanelStore;
    return panel?.kind === "artifact" ? panel.artifact : null;
  },
  /** Whatever the panel shows. */
  getPanelSnapshot(): ChatSidePanel | null {
    return chatArtifactPanelStore.panel;
  },
  /**
   * The search whose table the panel shows, null when it shows a document or
   * nothing. A string, so the progress of that search — a new panel object
   * per streamed row — reads the same.
   */
  getShownPeopleSearchId(): string | null {
    const { panel } = chatArtifactPanelStore;
    return panel?.kind === "peopleTable" ? panel.searchId : null;
  },
  subscribe(listener: () => void): () => void {
    chatArtifactPanelStore.listeners.add(listener);
    return () => chatArtifactPanelStore.listeners.delete(listener);
  },
  emit(): void {
    chatArtifactPanelStore.listeners.forEach((listener) => listener());
  },
  /** Shows `artifact`, at the latest version of it this conversation saw. */
  open(artifact: ActiveArtifact): void {
    chatArtifactPanelStore.panel = {
      kind: "artifact",
      artifact:
        chatArtifactPanelStore.latestById.get(artifact.artifactId) ?? artifact,
    };
    chatArtifactPanelStore.emit();
  },
  /**
   * Records a version just produced (a new document, or an edit of one) and
   * shows it — which also reloads the panel when it was already showing that
   * document.
   */
  publish(artifact: ActiveArtifact): void {
    chatArtifactPanelStore.latestById.set(artifact.artifactId, artifact);
    if (artifact.url) {
      chatArtifactPanelStore.publishedAtByUrl.set(artifact.url, Date.now());
    }
    chatArtifactPanelStore.panel = { kind: "artifact", artifact };
    chatArtifactPanelStore.emit();
  },
  /**
   * When `artifact`'s URL was received live — `undefined` for one read back
   * from the database, whose URL may be past its hour.
   */
  publishedAt(artifact: ActiveArtifact): number | undefined {
    return artifact.url
      ? chatArtifactPanelStore.publishedAtByUrl.get(artifact.url)
      : undefined;
  },
  /** Shows the people table of a search, filling it while `live` is set. */
  openPeopleTable(searchId: string, live: PeopleTableLive | null = null): void {
    chatArtifactPanelStore.panel = { kind: "peopleTable", searchId, live };
    chatArtifactPanelStore.emit();
  },
  /**
   * Pushes the progress of a running search, or null to hand its table over to
   * the saved rows. A no-op once the panel shows something else: the user
   * closing it, or opening another table, ends the live view.
   */
  updatePeopleTableLive(searchId: string, live: PeopleTableLive | null): void {
    const { panel } = chatArtifactPanelStore;
    if (!isPeopleTableShown(panel, searchId) || panel.live === live) return;
    chatArtifactPanelStore.panel = { kind: "peopleTable", searchId, live };
    chatArtifactPanelStore.emit();
  },
  /**
   * Closes the table of a search that found nobody: an empty table has
   * nothing to show. A no-op once the panel shows something else.
   */
  closePeopleTable(searchId: string): void {
    if (!isPeopleTableShown(chatArtifactPanelStore.panel, searchId)) return;
    chatArtifactPanelStore.close();
  },
  close(): void {
    if (chatArtifactPanelStore.panel === null) return;
    chatArtifactPanelStore.panel = null;
    chatArtifactPanelStore.emit();
  },
  /** Closes the panel and forgets every version: another conversation. */
  reset(): void {
    chatArtifactPanelStore.latestById.clear();
    chatArtifactPanelStore.publishedAtByUrl.clear();
    chatArtifactPanelStore.close();
  },
};

/** The document the panel shows, null when it shows a table or nothing. */
export function useChatArtifactPanel(): ActiveArtifact | null {
  return useSyncExternalStore(
    chatArtifactPanelStore.subscribe,
    chatArtifactPanelStore.getSnapshot,
  );
}

/**
 * The search whose table the side panel shows, or null. For a reader that only
 * needs to know which table is open: it does not render again on each row the
 * search streams, as a reader of `useChatSidePanel` does.
 */
export function useShownPeopleSearchId(): string | null {
  return useSyncExternalStore(
    chatArtifactPanelStore.subscribe,
    chatArtifactPanelStore.getShownPeopleSearchId,
  );
}

const getIsPanelOpen = (): boolean =>
  chatArtifactPanelStore.getPanelSnapshot() !== null;

/**
 * Whether the side panel shows anything. For a reader that only lays out
 * around it: it does not render again on each row a search streams.
 */
export function useIsChatSidePanelOpen(): boolean {
  return useSyncExternalStore(chatArtifactPanelStore.subscribe, getIsPanelOpen);
}

/** Whatever the side panel shows: a document, a table of people, or nothing. */
export function useChatSidePanel(): ChatSidePanel | null {
  return useSyncExternalStore(
    chatArtifactPanelStore.subscribe,
    chatArtifactPanelStore.getPanelSnapshot,
  );
}
